import {
  collection,
  doc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  setDoc,
  query,
  where,
  orderBy,
  Timestamp,
  writeBatch,
  type DocumentData,
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import type { Asset, AssetPayment, BtcApplication, Budget, Caixinha, CaixinhaLog, CaixinhaSnapshot, Conversion, CorporateAction, Earning, Expense, Income, InvestmentAsset, Tag, Trade, WishlistItem } from '../types';


// Collection names
const USER_PROFILES_COLLECTION = 'user_profiles';
const BUDGETS_COLLECTION = 'budgets';
const EXPENSES_COLLECTION = 'expenses';
const INCOMES_COLLECTION = 'incomes';
const ASSETS_COLLECTION = 'assets';
const ASSET_PAYMENTS_COLLECTION = 'asset_payments';
const TAGS_COLLECTION = 'tags';
const CAIXINHAS_COLLECTION = 'caixinhas';

// Helper to convert Firestore timestamp to Date
function timestampToDate(timestamp: Timestamp | Date): Date {
  if (timestamp instanceof Timestamp) {
    return timestamp.toDate();
  }
  return timestamp;
}

// ============================================
// USER PROFILE OPERATIONS
// ============================================

export async function writeUserProfile(
  uid: string,
  email: string,
  displayName: string | null
): Promise<void> {
  await setDoc(doc(db, USER_PROFILES_COLLECTION, uid), {
    uid,
    email,
    displayName: displayName ?? email,
  });
}

export async function getUserProfileByEmail(
  email: string
): Promise<{ uid: string; email: string; displayName: string } | null> {
  const q = query(
    collection(db, USER_PROFILES_COLLECTION),
    where('email', '==', email)
  );
  const snapshot = await getDocs(q);
  if (snapshot.empty) return null;
  const data = snapshot.docs[0]!.data();
  return { uid: data.uid, email: data.email, displayName: data.displayName };
}

// ============================================
// BUDGET OPERATIONS
// ============================================

export async function getBudget(
  userId: string,
  month: number,
  year: number
): Promise<Budget | null> {
  const q = query(
    collection(db, BUDGETS_COLLECTION),
    where('userId', '==', userId),
    where('month', '==', month),
    where('year', '==', year)
  );

  const snapshot = await getDocs(q);

  if (snapshot.empty) {
    return null;
  }

  const docData = snapshot.docs[0]!;
  return {
    id: docData.id,
    ...docData.data(),
  } as Budget;
}

export async function getAllBudgets(userId: string): Promise<Budget[]> {
  const q = query(
    collection(db, BUDGETS_COLLECTION),
    where('userId', '==', userId)
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }) as Budget);
}

export async function saveBudget(
  userId: string,
  budgetData: Omit<Budget, 'id' | 'userId'>
): Promise<Budget> {
  // Check if budget already exists for this month/year
  const existing = await getBudget(userId, budgetData.month, budgetData.year);

  if (existing) {
    // Update existing budget
    const docRef = doc(db, BUDGETS_COLLECTION, existing.id);
    await updateDoc(docRef, {
      needs: budgetData.needs,
      comfort: budgetData.comfort,
      education: budgetData.education,
      goals: budgetData.goals,
      freedom: budgetData.freedom,
    });
    return {
      ...existing,
      ...budgetData,
    };
  } else {
    // Create new budget
    const docRef = await addDoc(collection(db, BUDGETS_COLLECTION), {
      userId,
      ...budgetData,
    });
    return {
      id: docRef.id,
      userId,
      ...budgetData,
    };
  }
}

// ============================================
// EXPENSE OPERATIONS
// ============================================

export async function getAllExpenses(userId: string): Promise<Expense[]> {
  const q = query(
    collection(db, EXPENSES_COLLECTION),
    where('userId', '==', userId),
    orderBy('date', 'asc')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((docData) => {
    const data = docData.data();
    return {
      id: docData.id,
      ...data,
      date: timestampToDate(data.date),
      createdAt: timestampToDate(data.createdAt),
    } as Expense;
  });
}

export async function getExpenses(
  userId: string,
  month: number,
  year: number
): Promise<Expense[]> {
  // Calculate date range for the month
  const startDate = new Date(year, month - 1, 1);
  const endDate = new Date(year, month, 0, 23, 59, 59, 999);

  const q = query(
    collection(db, EXPENSES_COLLECTION),
    where('userId', '==', userId),
    where('date', '>=', Timestamp.fromDate(startDate)),
    where('date', '<=', Timestamp.fromDate(endDate)),
    orderBy('date', 'asc')
  );

  const snapshot = await getDocs(q);

  return snapshot.docs.map((docData) => {
    const data = docData.data();
    return {
      id: docData.id,
      ...data,
      date: timestampToDate(data.date),
      createdAt: timestampToDate(data.createdAt),
    } as Expense;
  });
}

export async function addExpense(
  userId: string,
  expenseData: Omit<Expense, 'id' | 'createdAt' | 'userId'>
): Promise<Expense> {
  const now = new Date();

  const docRef = await addDoc(collection(db, EXPENSES_COLLECTION), {
    userId,
    ...expenseData,
    date: Timestamp.fromDate(expenseData.date),
    createdAt: Timestamp.fromDate(now),
  });

  return {
    id: docRef.id,
    userId,
    ...expenseData,
    createdAt: now,
  };
}

export async function updateExpense(
  expenseId: string,
  expenseData: Partial<Expense>
): Promise<void> {
  const docRef = doc(db, EXPENSES_COLLECTION, expenseId);

  const updateData: Record<string, unknown> = { ...expenseData };

  // Convert date to Timestamp if present
  if (expenseData.date) {
    updateData.date = Timestamp.fromDate(expenseData.date);
  }

  // Remove fields that shouldn't be updated
  delete updateData.id;
  delete updateData.userId;
  delete updateData.createdAt;

  await updateDoc(docRef, updateData);
}

export async function deleteExpense(expenseId: string): Promise<void> {
  const docRef = doc(db, EXPENSES_COLLECTION, expenseId);
  await deleteDoc(docRef);
}

// ============================================
// INCOME OPERATIONS
// ============================================

export async function getAllIncomes(userId: string): Promise<Income[]> {
  const q = query(
    collection(db, INCOMES_COLLECTION),
    where('userId', '==', userId)
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((docData) => {
    const data = docData.data();
    return {
      id: docData.id,
      ...data,
      createdAt: timestampToDate(data.createdAt),
    } as Income;
  });
}

export async function getIncomes(
  userId: string,
  month: number,
  year: number
): Promise<Income[]> {
  const q = query(
    collection(db, INCOMES_COLLECTION),
    where('userId', '==', userId),
    where('month', '==', month),
    where('year', '==', year),
    orderBy('createdAt', 'desc')
  );

  const snapshot = await getDocs(q);

  return snapshot.docs.map((docData) => {
    const data = docData.data();
    return {
      id: docData.id,
      ...data,
      createdAt: timestampToDate(data.createdAt),
    } as Income;
  });
}

export async function addIncome(
  userId: string,
  incomeData: Omit<Income, 'id' | 'createdAt' | 'userId'>
): Promise<Income> {
  const now = new Date();

  const docRef = await addDoc(collection(db, INCOMES_COLLECTION), {
    userId,
    ...incomeData,
    createdAt: Timestamp.fromDate(now),
  });

  return {
    id: docRef.id,
    userId,
    ...incomeData,
    createdAt: now,
  };
}

export async function updateIncome(
  incomeId: string,
  incomeData: Partial<Income>
): Promise<void> {
  const docRef = doc(db, INCOMES_COLLECTION, incomeId);

  const updateData: Record<string, unknown> = { ...incomeData };

  // Remove fields that shouldn't be updated
  delete updateData.id;
  delete updateData.userId;
  delete updateData.createdAt;

  await updateDoc(docRef, updateData);
}

export async function deleteIncome(incomeId: string): Promise<void> {
  const docRef = doc(db, INCOMES_COLLECTION, incomeId);
  await deleteDoc(docRef);
}

// ============================================
// ASSET OPERATIONS
// ============================================

export async function getAssets(userId: string): Promise<Asset[]> {
  const q = query(
    collection(db, ASSETS_COLLECTION),
    where('userId', '==', userId),
    orderBy('createdAt', 'desc')
  );

  const snapshot = await getDocs(q);

  return snapshot.docs.map((docData) => {
    const data = docData.data();
    return {
      id: docData.id,
      ...data,
      createdAt: timestampToDate(data.createdAt),
    } as Asset;
  });
}

export async function addAsset(
  userId: string,
  assetData: Omit<Asset, 'id' | 'createdAt' | 'userId'>
): Promise<Asset> {
  const now = new Date();

  const docRef = await addDoc(collection(db, ASSETS_COLLECTION), {
    userId,
    ...assetData,
    createdAt: Timestamp.fromDate(now),
  });

  return {
    id: docRef.id,
    userId,
    ...assetData,
    createdAt: now,
  };
}

export async function updateAsset(
  assetId: string,
  assetData: Partial<Omit<Asset, 'id' | 'userId' | 'createdAt'>>
): Promise<void> {
  const docRef = doc(db, ASSETS_COLLECTION, assetId);
  await updateDoc(docRef, assetData);
}

export async function deleteAsset(assetId: string): Promise<void> {
  const docRef = doc(db, ASSETS_COLLECTION, assetId);
  await updateDoc(docRef, { deactivated: true });
}

// ============================================
// ASSET PAYMENT OPERATIONS
// ============================================

export async function getAssetPayments(
  userId: string,
  month: number,
  year: number
): Promise<AssetPayment[]> {
  const q = query(
    collection(db, ASSET_PAYMENTS_COLLECTION),
    where('userId', '==', userId),
    where('month', '==', month),
    where('year', '==', year),
    orderBy('createdAt', 'desc')
  );

  const snapshot = await getDocs(q);

  return snapshot.docs.map((docData) => {
    const data = docData.data();
    return {
      id: docData.id,
      ...data,
      createdAt: timestampToDate(data.createdAt),
    } as AssetPayment;
  });
}

export async function addAssetPayment(
  userId: string,
  paymentData: Omit<AssetPayment, 'id' | 'createdAt' | 'userId'>
): Promise<AssetPayment> {
  const now = new Date();

  const docRef = await addDoc(collection(db, ASSET_PAYMENTS_COLLECTION), {
    userId,
    ...paymentData,
    createdAt: Timestamp.fromDate(now),
  });

  return {
    id: docRef.id,
    userId,
    ...paymentData,
    createdAt: now,
  };
}

export async function updateAssetPayment(
  paymentId: string,
  paymentData: Partial<Omit<AssetPayment, 'id' | 'userId' | 'createdAt'>>
): Promise<void> {
  const docRef = doc(db, ASSET_PAYMENTS_COLLECTION, paymentId);
  await updateDoc(docRef, paymentData);
}

export async function deleteAssetPayment(paymentId: string): Promise<void> {
  const docRef = doc(db, ASSET_PAYMENTS_COLLECTION, paymentId);
  await deleteDoc(docRef);
}

// ============================================
// TAG OPERATIONS
// ============================================

export async function getTags(userId: string): Promise<Tag[]> {
  const q = query(
    collection(db, TAGS_COLLECTION),
    where('userId', '==', userId),
    orderBy('createdAt', 'asc')
  );

  const snapshot = await getDocs(q);

  return snapshot.docs.map((docData) => {
    const data = docData.data();
    return {
      id: docData.id,
      ...data,
      createdAt: timestampToDate(data.createdAt),
    } as Tag;
  });
}

export async function addTag(
  userId: string,
  tagData: { name: string; icon: string }
): Promise<Tag> {
  const now = new Date();

  const docRef = await addDoc(collection(db, TAGS_COLLECTION), {
    userId,
    ...tagData,
    createdAt: Timestamp.fromDate(now),
  });

  return {
    id: docRef.id,
    userId,
    ...tagData,
    createdAt: now,
  };
}

export async function updateTag(
  tagId: string,
  tagData: Partial<Omit<Tag, 'id' | 'createdAt'>>
): Promise<void> {
  const docRef = doc(db, TAGS_COLLECTION, tagId);
  await updateDoc(docRef, tagData);
}

export async function deleteTag(tagId: string): Promise<void> {
  const docRef = doc(db, TAGS_COLLECTION, tagId);
  await deleteDoc(docRef);
}

// ============================================
// CAIXINHA OPERATIONS
// ============================================

function serializeSnapshot(s: CaixinhaSnapshot) {
  return { ...s, date: Timestamp.fromDate(s.date) };
}

function serializeLog(l: CaixinhaLog) {
  return { ...l, date: Timestamp.fromDate(l.date) };
}

function deserializeCaixinha(docData: { id: string; data: () => Record<string, unknown> }): Caixinha {
  const data = docData.data();
  return {
    id: docData.id,
    userId: data.userId as string,
    title: data.title as string,
    description: data.description as string | undefined,
    createdAt: timestampToDate(data.createdAt as Timestamp | Date),
    snapshots: ((data.snapshots as Record<string, unknown>[]) ?? []).map((s) => ({
      id: s.id as string,
      date: timestampToDate(s.date as Timestamp | Date),
      value: s.value as number,
    })),
    log: ((data.log as Record<string, unknown>[]) ?? []).map((l) => ({
      id: l.id as string,
      date: timestampToDate(l.date as Timestamp | Date),
      value: l.value as number,
      description: l.description as string,
    })),
  };
}

export async function getCaixinhas(userId: string): Promise<Caixinha[]> {
  const q = query(
    collection(db, CAIXINHAS_COLLECTION),
    where('userId', '==', userId),
    orderBy('createdAt', 'asc')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => deserializeCaixinha({ id: d.id, data: d.data.bind(d) }));
}

export async function addCaixinha(
  userId: string,
  data: { title: string; description?: string }
): Promise<Caixinha> {
  const now = new Date();
  const docRef = await addDoc(collection(db, CAIXINHAS_COLLECTION), {
    userId,
    title: data.title,
    description: data.description ?? '',
    snapshots: [],
    log: [],
    createdAt: Timestamp.fromDate(now),
  });
  return {
    id: docRef.id,
    userId,
    title: data.title,
    description: data.description,
    snapshots: [],
    log: [],
    createdAt: now,
  };
}

export async function updateCaixinha(
  id: string,
  data: Partial<Pick<Caixinha, 'title' | 'description' | 'snapshots' | 'log'>>
): Promise<void> {
  const docRef = doc(db, CAIXINHAS_COLLECTION, id);
  const payload: Record<string, unknown> = {};
  if (data.title !== undefined) payload.title = data.title;
  if (data.description !== undefined) payload.description = data.description;
  if (data.snapshots !== undefined) payload.snapshots = data.snapshots.map(serializeSnapshot);
  if (data.log !== undefined) payload.log = data.log.map(serializeLog);
  await updateDoc(docRef, payload);
}

export async function deleteCaixinha(id: string): Promise<void> {
  await deleteDoc(doc(db, CAIXINHAS_COLLECTION, id));
}

// ============================================
// BTC APPLICATION OPERATIONS
// ============================================

const BTC_APPLICATIONS_COLLECTION = 'btc_applications';

export async function getBtcApplications(userId: string): Promise<BtcApplication[]> {
  const q = query(
    collection(db, BTC_APPLICATIONS_COLLECTION),
    where('userId', '==', userId),
    orderBy('date', 'asc')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => {
    const data = d.data();
    return {
      id: d.id,
      userId: data.userId as string,
      date: timestampToDate(data.date as Timestamp | Date),
      btcAmount: data.btcAmount as number,
      paidBrl: data.paidBrl as number,
      feeBtc: data.feeBtc as number,
      createdAt: timestampToDate(data.createdAt as Timestamp | Date),
    };
  });
}

export async function addBtcApplication(
  userId: string,
  data: Omit<BtcApplication, 'id' | 'userId' | 'createdAt'>
): Promise<BtcApplication> {
  const now = new Date();
  const docRef = await addDoc(collection(db, BTC_APPLICATIONS_COLLECTION), {
    userId,
    date: Timestamp.fromDate(data.date),
    btcAmount: data.btcAmount,
    paidBrl: data.paidBrl,
    feeBtc: data.feeBtc,
    createdAt: Timestamp.fromDate(now),
  });
  return { id: docRef.id, userId, ...data, createdAt: now };
}

export async function updateBtcApplication(
  id: string,
  data: Partial<Omit<BtcApplication, 'id' | 'userId' | 'createdAt'>>
): Promise<void> {
  const payload: Record<string, unknown> = {};
  if (data.date !== undefined) payload.date = Timestamp.fromDate(data.date);
  if (data.btcAmount !== undefined) payload.btcAmount = data.btcAmount;
  if (data.paidBrl !== undefined) payload.paidBrl = data.paidBrl;
  if (data.feeBtc !== undefined) payload.feeBtc = data.feeBtc;
  await updateDoc(doc(db, BTC_APPLICATIONS_COLLECTION, id), payload);
}

export async function deleteBtcApplication(id: string): Promise<void> {
  await deleteDoc(doc(db, BTC_APPLICATIONS_COLLECTION, id));
}

// ============================================
// WISHLIST OPERATIONS (shared between users)
// ============================================

const WISHLIST_COLLECTION = 'wishlist';

function wishlistFromDoc(d: { id: string; data: () => DocumentData }): WishlistItem {
  const data = d.data();
  // support legacy single `link` field migrated to `links` array
  const links: string[] | undefined =
    data.links ?? (data.link ? [data.link] : undefined);
  return {
    id: d.id,
    title: data.title,
    links,
    averagePrice: data.averagePrice,
    targetDate: timestampToDate(data.targetDate),
    order: data.order ?? 0,
    createdAt: timestampToDate(data.createdAt),
    addedBy: data.addedBy,
    completed: data.completed ?? false,
    completedAt: data.completedAt ? timestampToDate(data.completedAt) : undefined,
  };
}

export async function getWishlistItems(): Promise<WishlistItem[]> {
  const q = query(collection(db, WISHLIST_COLLECTION), orderBy('order', 'asc'));
  const snapshot = await getDocs(q);
  return snapshot.docs.map(wishlistFromDoc);
}

export async function addWishlistItem(
  item: Omit<WishlistItem, 'id'>
): Promise<WishlistItem> {
  const now = Timestamp.now();
  const payload = {
    title: item.title,
    links: item.links ?? [],
    averagePrice: item.averagePrice,
    targetDate: Timestamp.fromDate(item.targetDate),
    order: item.order,
    createdAt: now,
    addedBy: item.addedBy,
    completed: item.completed ?? false,
    completedAt: item.completedAt ? Timestamp.fromDate(item.completedAt) : null,
  };
  const docRef = await addDoc(collection(db, WISHLIST_COLLECTION), payload);
  return { id: docRef.id, ...item, createdAt: now.toDate() };
}

export async function updateWishlistItem(
  id: string,
  data: Partial<Omit<WishlistItem, 'id' | 'createdAt' | 'addedBy' | 'completedAt'>> & {
    completedAt?: Date | null;
  }
): Promise<void> {
  const payload: Record<string, unknown> = {};
  if (data.title !== undefined) payload.title = data.title;
  if (data.links !== undefined) payload.links = data.links;
  if (data.averagePrice !== undefined) payload.averagePrice = data.averagePrice;
  if (data.targetDate !== undefined) payload.targetDate = Timestamp.fromDate(data.targetDate);
  if (data.order !== undefined) payload.order = data.order;
  if (data.completed !== undefined) payload.completed = data.completed;
  if (data.completedAt !== undefined) {
    payload.completedAt = data.completedAt ? Timestamp.fromDate(data.completedAt) : null;
  }
  await updateDoc(doc(db, WISHLIST_COLLECTION, id), payload);
}

export async function deleteWishlistItem(id: string): Promise<void> {
  await deleteDoc(doc(db, WISHLIST_COLLECTION, id));
}

export async function reorderWishlistItems(
  updates: { id: string; order: number }[]
): Promise<void> {
  const batch = writeBatch(db);
  for (const { id, order } of updates) {
    batch.update(doc(db, WISHLIST_COLLECTION, id), { order });
  }
  await batch.commit();
}

// ============================================
// INVESTMENT OPERATIONS
// ============================================

const INVESTMENT_ASSETS_COLLECTION = 'investment_assets';
const TRADES_COLLECTION = 'trades';
const EARNINGS_COLLECTION = 'earnings';
const CORPORATE_ACTIONS_COLLECTION = 'corporate_actions';

function investmentAssetFromDoc(d: { id: string; data: () => DocumentData }): InvestmentAsset {
  const data = d.data();
  return {
    id: d.id,
    userId: data.userId,
    ticker: data.ticker,
    type: data.type,
    name: data.name ?? '',
    cnpj: data.cnpj || undefined,
    currentPrice: data.currentPrice ?? undefined,
    priceUpdatedAt: data.priceUpdatedAt ? timestampToDate(data.priceUpdatedAt) : undefined,
    deactivated: data.deactivated ?? false,
    createdAt: timestampToDate(data.createdAt),
  };
}

export async function getInvestmentAssets(userId: string): Promise<InvestmentAsset[]> {
  const q = query(
    collection(db, INVESTMENT_ASSETS_COLLECTION),
    where('userId', '==', userId),
    orderBy('ticker', 'asc')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(investmentAssetFromDoc);
}

export async function addInvestmentAsset(
  userId: string,
  data: Omit<InvestmentAsset, 'id' | 'userId' | 'createdAt'>
): Promise<InvestmentAsset> {
  const now = new Date();
  const docRef = await addDoc(collection(db, INVESTMENT_ASSETS_COLLECTION), {
    userId,
    ticker: data.ticker,
    type: data.type,
    name: data.name,
    cnpj: data.cnpj ?? '',
    currentPrice: data.currentPrice ?? null,
    priceUpdatedAt: data.priceUpdatedAt ? Timestamp.fromDate(data.priceUpdatedAt) : null,
    deactivated: false,
    createdAt: Timestamp.fromDate(now),
  });
  return { id: docRef.id, userId, ...data, createdAt: now };
}

export async function updateInvestmentAsset(
  id: string,
  data: Partial<Omit<InvestmentAsset, 'id' | 'userId' | 'createdAt'>>
): Promise<void> {
  const payload: Record<string, unknown> = {};
  if (data.ticker !== undefined) payload.ticker = data.ticker;
  if (data.type !== undefined) payload.type = data.type;
  if (data.name !== undefined) payload.name = data.name;
  if (data.cnpj !== undefined) payload.cnpj = data.cnpj ?? '';
  if (data.currentPrice !== undefined) payload.currentPrice = data.currentPrice ?? null;
  if (data.priceUpdatedAt !== undefined) {
    payload.priceUpdatedAt = data.priceUpdatedAt
      ? Timestamp.fromDate(data.priceUpdatedAt)
      : null;
  }
  if (data.deactivated !== undefined) payload.deactivated = data.deactivated;
  await updateDoc(doc(db, INVESTMENT_ASSETS_COLLECTION, id), payload);
}

/**
 * Apaga o papel e tudo que aponta para ele. Sem a cascata, operações e
 * proventos sobrevivem sem dono: somem da tela (o estado local filtra), mas
 * voltam no reload sem ticker — e a importação não consegue reconhecê-los
 * como duplicados, porque a deduplicação resolve o ticker pelo papel.
 */
export async function deleteInvestmentAsset(id: string): Promise<void> {
  const owned = await Promise.all([
    getDocs(query(collection(db, TRADES_COLLECTION), where('assetId', '==', id))),
    getDocs(query(collection(db, EARNINGS_COLLECTION), where('assetId', '==', id))),
    getDocs(query(collection(db, CORPORATE_ACTIONS_COLLECTION), where('assetId', '==', id))),
    getDocs(query(collection(db, CONVERSIONS_COLLECTION), where('fromAssetId', '==', id))),
    getDocs(query(collection(db, CONVERSIONS_COLLECTION), where('toAssetId', '==', id))),
  ]);

  const refs = owned.flatMap((snapshot) => snapshot.docs.map((d) => d.ref));
  refs.push(doc(db, INVESTMENT_ASSETS_COLLECTION, id));

  for (let i = 0; i < refs.length; i += BATCH_LIMIT) {
    const batch = writeBatch(db);
    for (const ref of refs.slice(i, i + BATCH_LIMIT)) batch.delete(ref);
    await batch.commit();
  }
}

/** Operação ou provento cujo papel não existe mais. */
export interface OrphanRecords {
  trades: Trade[];
  earnings: Earning[];
}

export async function deleteOrphanInvestmentRecords(
  orphans: OrphanRecords
): Promise<number> {
  const refs = [
    ...orphans.trades.map((t) => doc(db, TRADES_COLLECTION, t.id)),
    ...orphans.earnings.map((e) => doc(db, EARNINGS_COLLECTION, e.id)),
  ];

  for (let i = 0; i < refs.length; i += BATCH_LIMIT) {
    const batch = writeBatch(db);
    for (const ref of refs.slice(i, i + BATCH_LIMIT)) batch.delete(ref);
    await batch.commit();
  }
  return refs.length;
}

/** Grava as cotações buscadas na API num único round-trip. */
export async function updateInvestmentPrices(
  updates: { id: string; currentPrice: number }[]
): Promise<void> {
  if (updates.length === 0) return;
  const now = Timestamp.now();
  const batch = writeBatch(db);
  for (const { id, currentPrice } of updates) {
    batch.update(doc(db, INVESTMENT_ASSETS_COLLECTION, id), {
      currentPrice,
      priceUpdatedAt: now,
    });
  }
  await batch.commit();
}

function tradeFromDoc(d: { id: string; data: () => DocumentData }): Trade {
  const data = d.data();
  return {
    id: d.id,
    userId: data.userId,
    assetId: data.assetId,
    operation: data.operation,
    date: timestampToDate(data.date),
    quantity: data.quantity,
    unitPrice: data.unitPrice,
    fees: data.fees ?? 0,
    irrfWithheld: data.irrfWithheld ?? 0,
    notes: data.notes || undefined,
    createdAt: timestampToDate(data.createdAt),
  };
}

export async function getTrades(userId: string): Promise<Trade[]> {
  const q = query(
    collection(db, TRADES_COLLECTION),
    where('userId', '==', userId),
    orderBy('date', 'asc')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(tradeFromDoc);
}

export async function addTrade(
  userId: string,
  data: Omit<Trade, 'id' | 'userId' | 'createdAt'>
): Promise<Trade> {
  const now = new Date();
  const docRef = await addDoc(collection(db, TRADES_COLLECTION), {
    userId,
    assetId: data.assetId,
    operation: data.operation,
    date: Timestamp.fromDate(data.date),
    quantity: data.quantity,
    unitPrice: data.unitPrice,
    fees: data.fees,
    irrfWithheld: data.irrfWithheld ?? 0,
    notes: data.notes ?? '',
    createdAt: Timestamp.fromDate(now),
  });
  return { id: docRef.id, userId, ...data, createdAt: now };
}

export async function updateTrade(
  id: string,
  data: Partial<Omit<Trade, 'id' | 'userId' | 'createdAt'>>
): Promise<void> {
  const payload: Record<string, unknown> = {};
  if (data.assetId !== undefined) payload.assetId = data.assetId;
  if (data.operation !== undefined) payload.operation = data.operation;
  if (data.date !== undefined) payload.date = Timestamp.fromDate(data.date);
  if (data.quantity !== undefined) payload.quantity = data.quantity;
  if (data.unitPrice !== undefined) payload.unitPrice = data.unitPrice;
  if (data.fees !== undefined) payload.fees = data.fees;
  if (data.irrfWithheld !== undefined) payload.irrfWithheld = data.irrfWithheld;
  if (data.notes !== undefined) payload.notes = data.notes ?? '';
  await updateDoc(doc(db, TRADES_COLLECTION, id), payload);
}

export async function deleteTrade(id: string): Promise<void> {
  await deleteDoc(doc(db, TRADES_COLLECTION, id));
}

function earningFromDoc(d: { id: string; data: () => DocumentData }): Earning {
  const data = d.data();
  return {
    id: d.id,
    userId: data.userId,
    assetId: data.assetId,
    kind: data.kind,
    paymentDate: timestampToDate(data.paymentDate),
    quantityBase: data.quantityBase ?? undefined,
    valuePerShare: data.valuePerShare ?? undefined,
    grossValue: data.grossValue,
    irrfWithheld: data.irrfWithheld ?? 0,
    createdAt: timestampToDate(data.createdAt),
  };
}

export async function getEarnings(userId: string): Promise<Earning[]> {
  const q = query(
    collection(db, EARNINGS_COLLECTION),
    where('userId', '==', userId),
    orderBy('paymentDate', 'asc')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(earningFromDoc);
}

export async function addEarning(
  userId: string,
  data: Omit<Earning, 'id' | 'userId' | 'createdAt'>
): Promise<Earning> {
  const now = new Date();
  const docRef = await addDoc(collection(db, EARNINGS_COLLECTION), {
    userId,
    assetId: data.assetId,
    kind: data.kind,
    paymentDate: Timestamp.fromDate(data.paymentDate),
    quantityBase: data.quantityBase ?? null,
    valuePerShare: data.valuePerShare ?? null,
    grossValue: data.grossValue,
    irrfWithheld: data.irrfWithheld,
    createdAt: Timestamp.fromDate(now),
  });
  return { id: docRef.id, userId, ...data, createdAt: now };
}

export async function updateEarning(
  id: string,
  data: Partial<Omit<Earning, 'id' | 'userId' | 'createdAt'>>
): Promise<void> {
  const payload: Record<string, unknown> = {};
  if (data.assetId !== undefined) payload.assetId = data.assetId;
  if (data.kind !== undefined) payload.kind = data.kind;
  if (data.paymentDate !== undefined) payload.paymentDate = Timestamp.fromDate(data.paymentDate);
  if (data.quantityBase !== undefined) payload.quantityBase = data.quantityBase ?? null;
  if (data.valuePerShare !== undefined) payload.valuePerShare = data.valuePerShare ?? null;
  if (data.grossValue !== undefined) payload.grossValue = data.grossValue;
  if (data.irrfWithheld !== undefined) payload.irrfWithheld = data.irrfWithheld;
  await updateDoc(doc(db, EARNINGS_COLLECTION, id), payload);
}

export async function deleteEarning(id: string): Promise<void> {
  await deleteDoc(doc(db, EARNINGS_COLLECTION, id));
}

function corporateActionFromDoc(d: { id: string; data: () => DocumentData }): CorporateAction {
  const data = d.data();
  return {
    id: d.id,
    userId: data.userId,
    assetId: data.assetId,
    date: timestampToDate(data.date),
    factor: data.factor,
    notes: data.notes || undefined,
    createdAt: timestampToDate(data.createdAt),
  };
}

export async function getCorporateActions(userId: string): Promise<CorporateAction[]> {
  const q = query(
    collection(db, CORPORATE_ACTIONS_COLLECTION),
    where('userId', '==', userId),
    orderBy('date', 'asc')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(corporateActionFromDoc);
}

export async function addCorporateAction(
  userId: string,
  data: Omit<CorporateAction, 'id' | 'userId' | 'createdAt'>
): Promise<CorporateAction> {
  const now = new Date();
  const docRef = await addDoc(collection(db, CORPORATE_ACTIONS_COLLECTION), {
    userId,
    assetId: data.assetId,
    date: Timestamp.fromDate(data.date),
    factor: data.factor,
    notes: data.notes ?? '',
    createdAt: Timestamp.fromDate(now),
  });
  return { id: docRef.id, userId, ...data, createdAt: now };
}

export async function deleteCorporateAction(id: string): Promise<void> {
  await deleteDoc(doc(db, CORPORATE_ACTIONS_COLLECTION, id));
}

// ============================================
// IMPORTAÇÃO EM LOTE (relatório da B3)
// ============================================

/** O Firestore aceita até 500 operações por batch. */
const BATCH_LIMIT = 450;

export interface ImportInvestmentsInput {
  newAssets: { ticker: string; name: string; type: InvestmentAsset['type'] }[];
  trades: (Omit<Trade, 'id' | 'userId' | 'createdAt'> & { ticker: string })[];
  earnings: (Omit<Earning, 'id' | 'userId' | 'createdAt'> & { ticker: string })[];
  /** Ticker (maiúsculo) -> id dos papéis já cadastrados. */
  existingTickers: Record<string, string>;
}

export interface ImportInvestmentsResult {
  assets: InvestmentAsset[];
  trades: Trade[];
  earnings: Earning[];
}

/**
 * Grava papéis, operações e proventos de uma vez. Os ids são gerados no
 * cliente para que operação e provento já saiam ligados ao papel novo dentro
 * do mesmo batch, sem ida e volta por documento.
 */
export async function importInvestments(
  userId: string,
  input: ImportInvestmentsInput
): Promise<ImportInvestmentsResult> {
  const now = new Date();
  const timestamp = Timestamp.fromDate(now);

  const writes: { ref: ReturnType<typeof doc>; data: Record<string, unknown> }[] = [];
  const tickerToId: Record<string, string> = { ...input.existingTickers };

  const createdAssets: InvestmentAsset[] = [];
  for (const asset of input.newAssets) {
    const ref = doc(collection(db, INVESTMENT_ASSETS_COLLECTION));
    tickerToId[asset.ticker.toUpperCase()] = ref.id;
    writes.push({
      ref,
      data: {
        userId,
        ticker: asset.ticker,
        type: asset.type,
        name: asset.name,
        cnpj: '',
        currentPrice: null,
        priceUpdatedAt: null,
        deactivated: false,
        createdAt: timestamp,
      },
    });
    createdAssets.push({
      id: ref.id,
      userId,
      ticker: asset.ticker,
      type: asset.type,
      name: asset.name,
      cnpj: undefined,
      createdAt: now,
    });
  }

  const createdTrades: Trade[] = [];
  for (const trade of input.trades) {
    const assetId = tickerToId[trade.ticker.toUpperCase()];
    if (!assetId) continue;
    const ref = doc(collection(db, TRADES_COLLECTION));
    writes.push({
      ref,
      data: {
        userId,
        assetId,
        operation: trade.operation,
        date: Timestamp.fromDate(trade.date),
        quantity: trade.quantity,
        unitPrice: trade.unitPrice,
        fees: trade.fees,
        irrfWithheld: trade.irrfWithheld ?? 0,
        notes: trade.notes ?? '',
        createdAt: timestamp,
      },
    });
    createdTrades.push({
      id: ref.id,
      userId,
      assetId,
      operation: trade.operation,
      date: trade.date,
      quantity: trade.quantity,
      unitPrice: trade.unitPrice,
      fees: trade.fees,
      irrfWithheld: trade.irrfWithheld ?? 0,
      notes: trade.notes,
      createdAt: now,
    });
  }

  const createdEarnings: Earning[] = [];
  for (const earning of input.earnings) {
    const assetId = tickerToId[earning.ticker.toUpperCase()];
    if (!assetId) continue;
    const ref = doc(collection(db, EARNINGS_COLLECTION));
    writes.push({
      ref,
      data: {
        userId,
        assetId,
        kind: earning.kind,
        paymentDate: Timestamp.fromDate(earning.paymentDate),
        quantityBase: earning.quantityBase ?? null,
        valuePerShare: earning.valuePerShare ?? null,
        grossValue: earning.grossValue,
        irrfWithheld: earning.irrfWithheld,
        createdAt: timestamp,
      },
    });
    createdEarnings.push({
      id: ref.id,
      userId,
      assetId,
      kind: earning.kind,
      paymentDate: earning.paymentDate,
      quantityBase: earning.quantityBase,
      valuePerShare: earning.valuePerShare,
      grossValue: earning.grossValue,
      irrfWithheld: earning.irrfWithheld,
      createdAt: now,
    });
  }

  for (let i = 0; i < writes.length; i += BATCH_LIMIT) {
    const batch = writeBatch(db);
    for (const write of writes.slice(i, i + BATCH_LIMIT)) {
      batch.set(write.ref, write.data);
    }
    await batch.commit();
  }

  return { assets: createdAssets, trades: createdTrades, earnings: createdEarnings };
}

// ============================================
// CONVERSÕES (incorporação / troca de ticker)
// ============================================

const CONVERSIONS_COLLECTION = 'conversions';

function conversionFromDoc(d: { id: string; data: () => DocumentData }): Conversion {
  const data = d.data();
  return {
    id: d.id,
    userId: data.userId,
    fromAssetId: data.fromAssetId,
    toAssetId: data.toAssetId,
    date: timestampToDate(data.date),
    fromQuantity: data.fromQuantity,
    toQuantity: data.toQuantity,
    notes: data.notes || undefined,
    createdAt: timestampToDate(data.createdAt),
  };
}

export async function getConversions(userId: string): Promise<Conversion[]> {
  const q = query(
    collection(db, CONVERSIONS_COLLECTION),
    where('userId', '==', userId),
    orderBy('date', 'asc')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(conversionFromDoc);
}

export async function addConversion(
  userId: string,
  data: Omit<Conversion, 'id' | 'userId' | 'createdAt'>
): Promise<Conversion> {
  const now = new Date();
  const docRef = await addDoc(collection(db, CONVERSIONS_COLLECTION), {
    userId,
    fromAssetId: data.fromAssetId,
    toAssetId: data.toAssetId,
    date: Timestamp.fromDate(data.date),
    fromQuantity: data.fromQuantity,
    toQuantity: data.toQuantity,
    notes: data.notes ?? '',
    createdAt: Timestamp.fromDate(now),
  });
  return { id: docRef.id, userId, ...data, createdAt: now };
}

export async function deleteConversion(id: string): Promise<void> {
  await deleteDoc(doc(db, CONVERSIONS_COLLECTION, id));
}

/** Troca o tipo de vários proventos de uma vez (reparo de classificação). */
export async function updateEarningKinds(
  updates: { id: string; kind: Earning['kind'] }[]
): Promise<number> {
  for (let i = 0; i < updates.length; i += BATCH_LIMIT) {
    const batch = writeBatch(db);
    for (const { id, kind } of updates.slice(i, i + BATCH_LIMIT)) {
      batch.update(doc(db, EARNINGS_COLLECTION, id), { kind });
    }
    await batch.commit();
  }
  return updates.length;
}
